import { useEffect, useState, useRef } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Cell,
  ComposedChart,
  Line,
  CartesianGrid,
  Legend,
  AreaChart,
  Area,
} from "recharts";
import {
  MessageSquare,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  Pill,
  HeartPulse,
  HelpCircle,
  Loader2,
  UserPlus,
  Mail,
  Check,
  Eye,
  EyeOff,
  Plus,
  CreditCard,
  RotateCcw,
  ImagePlus,
  X,
  FileText,
  Stethoscope,
  Clock,
  TrendingUp,
  Trophy,
  DollarSign,
  Target,
  Filter,
  Download,
  Trash2,
} from "lucide-react";
import { getDashboard, updateDoctorStatus, addLibraryTopic, refundDoctorRequest, fileUrl, uploadPhoto, getAdminSettings, saveAdminSettings, sendTestDigest, adminLogin, adminMe, adminLogout, getAdminAudit, exportAuditCsv, clearAuditLog, listPhysicians, addPhysician, togglePhysician, reassignRequest, getSubscribers, exportSubscribersCsv, sendBroadcast, getBroadcasts, cancelBroadcast, draftBroadcast, resendConfirmation, updateScheduled, getRecurring, saveRecurring, testSend, getOpenHours, getQuietHours, saveQuietHours, runAbDemo, getWeeklyDigest, setWeeklyDigest, sendWeeklyDigestNow, previewWeeklyDigest, getAdminQuestions, exportQuestionsCsv } from "../lib/api";
import { toast } from "sonner";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Lock, LogOut, ScrollText, Sparkles, Zap, MoonStar, FlaskConical, Link2, Send, Users, CalendarDays, PartyPopper, Languages } from "lucide-react";

function waitingInfo(dateStr) {
  const ms = Date.now() - new Date(dateStr).getTime();
  const hrs = ms / 3600000;
  let label;
  if (hrs < 1) label = `${Math.max(1, Math.round(ms / 60000))}m`;
  else if (hrs < 24) label = `${Math.round(hrs)}h`;
  else label = `${Math.round(hrs / 24)}d`;
  const cls = hrs >= 48 ? "bg-red-100 text-red-700" : hrs >= 24 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600";
  return { label, cls };
}

const StatCard = ({ icon: Icon, label, value, tone, testId, onClick }) => (
  <div
    className={`rounded-2xl border-2 p-5 bg-white ${tone} ${onClick ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-transform" : ""}`}
    data-testid={testId}
    onClick={onClick}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
  >
    <div className="flex items-center gap-3">
      <span className="grid place-items-center w-11 h-11 rounded-xl bg-slate-100">
        <Icon className="w-6 h-6 text-slate-700" />
      </span>
      <div>
        <p className="text-3xl font-heading font-extrabold text-slate-900">{value}</p>
        <p className="text-base text-slate-500 font-medium">{label}{onClick && <span className="ml-1 text-xs text-teal-600 font-semibold">· view</span>}</p>
      </div>
    </div>
  </div>
);

const ListCard = ({ title, icon: Icon, items, emptyText, testId }) => (
  <div className="rounded-2xl border-2 border-slate-200 bg-white p-5" data-testid={testId}>
    <h3 className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2 mb-4">
      <Icon className="w-5 h-5 text-teal-600" /> {title}
    </h3>
    {items && items.length > 0 ? (
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex items-center justify-between gap-3 text-base">
            <span className="text-slate-700 truncate">{it.label || it.question_text}</span>
            {it.count != null && (
              <span className="shrink-0 bg-teal-100 text-teal-800 font-bold text-sm px-2.5 py-1 rounded-full">
                {it.count}
              </span>
            )}
          </li>
        ))}
      </ul>
    ) : (
      <p className="text-base text-slate-400">{emptyText}</p>
    )}
  </div>
);

const COLORS = ["#0d9488", "#0284c7", "#0f766e", "#0369a1", "#14b8a6", "#38bdf8", "#115e59", "#075985"];

const fmtDay = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

const hourLabel = (h) => {
  const d = new Date();
  d.setHours(h, 0, 0, 0);
  return d.toLocaleTimeString(undefined, { hour: "numeric" });
};

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-md text-sm">
      <p className="font-bold text-slate-800 mb-1">{fmtDay(label)}</p>
      <p className="text-sky-600">{row.clicks} upsell clicks</p>
      <p className="text-teal-700">{row.reviews} paid reviews</p>
      <p className="text-emerald-700 font-semibold">{row.rate}% conversion</p>
    </div>
  );
};

export default function AdminPage() {
  const [authed, setAuthed] = useState(null); // null=checking, true, false
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [audit, setAudit] = useState([]);
  const [physicians, setPhysicians] = useState([]);
  const [newPhysName, setNewPhysName] = useState("");
  const [newPhysEmail, setNewPhysEmail] = useState("");
  const [newPhysTier, setNewPhysTier] = useState("all");
  const [addingPhys, setAddingPhys] = useState(false);
  const [reassigning, setReassigning] = useState({});
  const [auditAction, setAuditAction] = useState("");
  const [auditStart, setAuditStart] = useState("");
  const [auditEnd, setAuditEnd] = useState("");
  const [exportingAudit, setExportingAudit] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReviewed, setShowReviewed] = useState(false);
  const [labOnly, setLabOnly] = useState(false);
  const [addedTopics, setAddedTopics] = useState({});
  const [refunding, setRefunding] = useState({});
  const [refundFormId, setRefundFormId] = useState(null);
  const [refundReason, setRefundReason] = useState("");
  const [refundNotify, setRefundNotify] = useState(true);
  const [reviewFormId, setReviewFormId] = useState(null);
  const [reviewNote, setReviewNote] = useState("");
  const [reviewing, setReviewing] = useState({});
  const [reviewPhotos, setReviewPhotos] = useState([]);
  const reviewFileRef = useRef(null);
  const [settings, setSettings] = useState(null);
  const [savingHour, setSavingHour] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalInput, setGoalInput] = useState("");
  const [savingGoal, setSavingGoal] = useState(false);

  useEffect(() => {
    adminMe().then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  useEffect(() => {
    if (authed !== true) return;
    getDashboard()
      .then(setData)
      .catch((e) => { if (e?.response?.status === 401) setAuthed(false); })
      .finally(() => setLoading(false));
    getAdminSettings()
      .then(setSettings)
      .catch(() => setSettings({}));
    getAdminAudit().then(setAudit).catch(() => setAudit([]));
    loadPhysicians();
  }, [authed]);

  const loadPhysicians = () => {
    listPhysicians().then(setPhysicians).catch(() => setPhysicians([]));
  };

  const handleAddPhysician = async () => {
    if (!newPhysName.trim() || !newPhysEmail.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setAddingPhys(true);
    try {
      const tiers = newPhysTier === "all" ? ["all"] : [newPhysTier];
      const res = await addPhysician(newPhysName.trim(), newPhysEmail.trim(), tiers);
      if (res.ok) {
        toast.success("Physician added.");
        setNewPhysName("");
        setNewPhysEmail("");
        setNewPhysTier("all");
        loadPhysicians();
      } else {
        toast.error(res.error === "exists" ? "That email is already on the roster." : "Could not add physician.");
      }
    } catch {
      toast.error("Could not add physician.");
    } finally {
      setAddingPhys(false);
    }
  };

  const handleTogglePhysician = async (p) => {
    try {
      await togglePhysician(p.physician_id, !p.active);
      loadPhysicians();
    } catch {
      toast.error("Could not update physician.");
    }
  };

  const handleReassign = async (requestId, physicianId) => {
    if (!physicianId) return;
    setReassigning((s) => ({ ...s, [requestId]: true }));
    try {
      const res = await reassignRequest(requestId, physicianId);
      if (res.ok) {
        toast.success(`Reassigned to ${res.name}.`);
        getDashboard().then(setData).catch(() => {});
      } else {
        toast.error("Could not reassign.");
      }
    } catch {
      toast.error("Could not reassign.");
    } finally {
      setReassigning((s) => ({ ...s, [requestId]: false }));
    }
  };

  const auditFilters = () => ({
    action: auditAction || undefined,
    start: auditStart || undefined,
    end: auditEnd || undefined,
  });

  const loadAudit = () => {
    getAdminAudit(auditFilters()).then(setAudit).catch(() => setAudit([]));
  };

  const clearAuditFilters = () => {
    setAuditAction("");
    setAuditStart("");
    setAuditEnd("");
    getAdminAudit().then(setAudit).catch(() => setAudit([]));
  };

  const [clearingLog, setClearingLog] = useState(false);
  const handleClearLog = async () => {
    if (!window.confirm("Clear ALL admin activity log entries? This cannot be undone.")) return;
    setClearingLog(true);
    try {
      const res = await clearAuditLog();
      toast.success(`Cleared ${res.deleted} log ${res.deleted === 1 ? "entry" : "entries"}.`);
      setAudit([]);
    } catch {
      toast.error("Could not clear the log.");
    } finally {
      setClearingLog(false);
    }
  };

  const exportAudit = async () => {
    setExportingAudit(true);
    try {
      const blob = await exportAuditCsv(auditFilters());
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "admin-activity.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Activity log exported.");
    } catch (e) {
      toast.error("Could not export the log.");
    } finally {
      setExportingAudit(false);
    }
  };

  const [subscribers, setSubscribers] = useState([]);
  const [exportingSubs, setExportingSubs] = useState(false);
  const [bcSubject, setBcSubject] = useState("");
  const [bcMessage, setBcMessage] = useState("");
  const [sendingBc, setSendingBc] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const [bcConfirmOpen, setBcConfirmOpen] = useState(false);
  const [bcSchedule, setBcSchedule] = useState("");
  const [draftingBc, setDraftingBc] = useState(false);
  const [bcDetail, setBcDetail] = useState(null);
  const [resendingEmail, setResendingEmail] = useState("");
  const [bcSegment, setBcSegment] = useState("all");
  const [editingBid, setEditingBid] = useState(null);
  const [recurring, setRecurring] = useState(null);
  const [savingRec, setSavingRec] = useState(false);
  const [bcSubjectB, setBcSubjectB] = useState("");
  const [testingBc, setTestingBc] = useState(false);
  const [openHours, setOpenHours] = useState(null);
  const [optimizeTime, setOptimizeTime] = useState(false);
  const [runningDemo, setRunningDemo] = useState(false);
  const [quietCfg, setQuietCfg] = useState(null);
  const [savingQuiet, setSavingQuiet] = useState(false);
  const [linkA, setLinkA] = useState("");
  const [linkB, setLinkB] = useState("");
  const [digestCfg, setDigestCfg] = useState(null);
  const [digestSending, setDigestSending] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const [previewingDigest, setPreviewingDigest] = useState(false);
  const [statDetail, setStatDetail] = useState(null);
  const [statRows, setStatRows] = useState(null);
  const [statDays, setStatDays] = useState(0);
  const [statCategory, setStatCategory] = useState("");
  const [statTranslate, setStatTranslate] = useState(false);
  const [exportingStat, setExportingStat] = useState(false);
  const loadStatRows = async (type, days, category, translate) => {
    setStatRows(null);
    try {
      const res = await getAdminQuestions(type === "emergency", 300, days, category, translate);
      setStatRows(res.questions || []);
    } catch {
      setStatRows([]);
      toast.error("Could not load the details.");
    }
  };
  const openStat = async (type) => {
    const titles = { questions: "All questions asked", emergency: "Emergency-flagged questions", visits: "Site visits — last 30 days" };
    setStatDetail({ type, title: titles[type] });
    setStatDays(0);
    setStatCategory("");
    setStatTranslate(false);
    if (type === "visits") { setStatRows(null); return; }
    await loadStatRows(type, 0, "", false);
  };
  const changeStatRange = async (days) => {
    setStatDays(days);
    if (statDetail && statDetail.type !== "visits") await loadStatRows(statDetail.type, days, statCategory, statTranslate);
  };
  const changeStatCategory = async (category) => {
    setStatCategory(category);
    if (statDetail && statDetail.type !== "visits") await loadStatRows(statDetail.type, statDays, category, statTranslate);
  };
  const toggleStatTranslate = async () => {
    const next = !statTranslate;
    setStatTranslate(next);
    if (statDetail && statDetail.type !== "visits") await loadStatRows(statDetail.type, statDays, statCategory, next);
  };
  const exportStatCsv = async () => {
    if (exportingStat) return;
    setExportingStat(true);
    try {
      const emergency = statDetail?.type === "emergency";
      const blob = await exportQuestionsCsv(emergency, 500, statDays, statCategory, statTranslate);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = emergency ? "emergency-questions.csv" : "questions.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Questions exported.");
    } catch {
      toast.error("Could not export questions.");
    } finally {
      setExportingStat(false);
    }
  };
  const TZ_OPTIONS = [
    { v: "America/New_York", l: "Eastern (ET)" },
    { v: "America/Chicago", l: "Central (CT)" },
    { v: "America/Denver", l: "Mountain (MT)" },
    { v: "America/Los_Angeles", l: "Pacific (PT)" },
    { v: "UTC", l: "UTC" },
  ];
  const segments = Array.from(new Set(subscribers.map((s) => s.source).filter(Boolean)));
  const segmentCount = subscribers.filter((s) => s.confirmed && !s.unsubscribed && (bcSegment === "all" || s.source === bcSegment)).length;
  const DOW_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const MILESTONES = [50, 100, 250, 500, 1000, 2500, 5000];
  const previewDigest = async () => {
    if (previewingDigest) return;
    setPreviewingDigest(true);
    try {
      const res = await previewWeeklyDigest();
      setDigestPreview(res);
    } catch {
      toast.error("Could not load the digest preview.");
    } finally {
      setPreviewingDigest(false);
    }
  };
  useEffect(() => {
    if (authed) {
      getRecurring().then((d) => setRecurring(d.recurring || null)).catch(() => {});
      getOpenHours().then((d) => setOpenHours(d)).catch(() => {});
      getQuietHours().then((d) => setQuietCfg(d)).catch(() => {});
      getWeeklyDigest().then((d) => setDigestCfg(d)).catch(() => {});
    }
  }, [authed]);
  const runDemo = async () => {
    if (runningDemo) return;
    setRunningDemo(true);
    try {
      const res = await runAbDemo({ subject: bcSubject || "Demo subject A", subject_b: bcSubjectB || "Demo subject B", message: bcMessage || "" });
      if (res.ok) {
        toast.success(`A/B demo sent to ${res.to} — check your inbox for both variants, the winner, and the winner alert.`);
        getBroadcasts().then((d) => setBroadcasts(d.broadcasts || [])).catch(() => {});
      } else {
        toast.error("Email isn't configured, so the demo couldn't send.");
      }
    } catch {
      toast.error("Could not run the A/B demo.");
    } finally {
      setRunningDemo(false);
    }
  };
  const saveQuiet = async () => {
    if (!quietCfg || savingQuiet) return;
    setSavingQuiet(true);
    try {
      const res = await saveQuietHours({ start: Number(quietCfg.start), end: Number(quietCfg.end), tz: quietCfg.tz });
      setQuietCfg(res);
      toast.success("Quiet hours saved.");
    } catch {
      toast.error("Could not save quiet hours.");
    } finally {
      setSavingQuiet(false);
    }
  };
  const toggleDigest = async (active) => {
    try {
      const res = await setWeeklyDigest(active);
      setDigestCfg(res);
      toast.success(active ? "Weekly digest on — you'll get it every Monday." : "Weekly digest off.");
    } catch {
      toast.error("Could not update the weekly digest.");
    }
  };
  const sendDigestNow = async () => {
    if (digestSending) return;
    setDigestSending(true);
    try {
      const res = await sendWeeklyDigestNow();
      toast[res.ok ? "success" : "error"](res.ok ? `Digest sent to ${res.to}.` : "Could not send the digest (email not configured).");
    } catch {
      toast.error("Could not send the digest.");
    } finally {
      setDigestSending(false);
    }
  };
  const growthData = (() => {
    const confirmed = subscribers
      .filter((s) => s.confirmed && s.subscribed_at)
      .map((s) => new Date(s.subscribed_at).toISOString().slice(0, 10))
      .sort();
    let cum = 0;
    const byDay = {};
    confirmed.forEach((d) => { cum += 1; byDay[d] = cum; });
    return Object.entries(byDay).map(([date, total]) => ({ date: date.slice(5), total }));
  })();
  const bestHourLabel = openHours && openHours.best_hour_utc != null
    ? new Date(Date.UTC(2020, 0, 1, openHours.best_hour_utc)).toLocaleTimeString([], { hour: "numeric" })
    : null;
  const bestDowLabel = openHours && openHours.best_dow != null ? DOW_NAMES[openHours.best_dow] : null;
  const rateData = broadcasts
    .filter((b) => b.status === "sent" && (b.recipients || 0) > 0)
    .slice(0, 12)
    .reverse()
    .map((b) => ({
      name: (b.subject || "").slice(0, 14),
      open: Math.round(((b.opens || 0) / b.recipients) * 100),
      click: Math.round(((b.clicks || 0) / b.recipients) * 100),
    }));
  const testBc = async () => {
    if (!bcSubject.trim() || !bcMessage.trim() || testingBc) return;
    setTestingBc(true);
    try {
      const res = await testSend({ subject: bcSubject, message: bcMessage });
      toast[res.sent ? "success" : "error"](res.sent ? `Test sent to ${res.to}.` : "Could not send test.");
    } catch {
      toast.error("Could not send test.");
    } finally {
      setTestingBc(false);
    }
  };
  const confirmedCount = subscribers.filter((s) => s.confirmed && !s.unsubscribed).length;
  const milestoneReached = [...MILESTONES].reverse().find((m) => confirmedCount >= m) || null;
  useEffect(() => {
    if (authed) {
      getBroadcasts().then((d) => setBroadcasts(d.broadcasts || [])).catch(() => {});
    }
  }, [authed]);
  useEffect(() => {
    if (authed) {
      getSubscribers().then((d) => setSubscribers(d.subscribers || [])).catch(() => {});
    }
  }, [authed]);
  const exportSubs = async () => {
    setExportingSubs(true);
    try {
      const blob = await exportSubscribersCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "subscribers.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Subscribers exported.");
    } catch {
      toast.error("Could not export subscribers.");
    } finally {
      setExportingSubs(false);
    }
  };

  const sendBc = async () => {
    setBcConfirmOpen(false);
    if (!bcSubject.trim() || !bcMessage.trim() || sendingBc) return;
    if (!editingBid && linkA.trim() && linkB.trim() && !bcMessage.includes("{{link}}")) {
      toast.error("Add {{link}} in your message so the A/B link can be placed.");
      return;
    }
    setSendingBc(true);
    try {
      if (editingBid) {
        const res = await updateScheduled({ bid: editingBid, subject: bcSubject, message: bcMessage, scheduled_for: bcSchedule ? new Date(bcSchedule).toISOString() : "", segment: bcSegment });
        toast.success("Scheduled broadcast updated.");
        if (res.shifted && res.scheduled_for) toast("Moved into daytime hours (8 AM–8 PM ET) — now " + new Date(res.scheduled_for).toLocaleString());
      } else {
        const payload = { subject: bcSubject, message: bcMessage, segment: bcSegment };
        if (bcSchedule) payload.scheduled_for = new Date(bcSchedule).toISOString();
        if (bcSubjectB.trim()) payload.subject_b = bcSubjectB.trim();
        if (linkA.trim() && linkB.trim()) { payload.link_a = linkA.trim(); payload.link_b = linkB.trim(); }
        if (optimizeTime && !bcSchedule && !bcSubjectB.trim() && !(linkA.trim() && linkB.trim())) payload.optimize_time = true;
        const res = await sendBroadcast(payload);
        toast.success(res.optimized ? "Scheduled for the best send time." : res.scheduled ? "Broadcast scheduled." : (res.ab && res.kind === "link") ? "Link A/B test started — the winning link auto-sends to the rest." : res.ab ? `A/B test started — winner auto-sends to the rest.` : `Sent to ${res.sent} of ${res.total} subscriber(s).`);
        if ((res.optimized || res.shifted) && res.scheduled_for) toast((res.optimized ? "Best send time: " : "Moved into daytime hours — ") + new Date(res.scheduled_for).toLocaleString());
      }
      setBcSubject(""); setBcMessage(""); setBcSchedule(""); setBcSegment("all"); setBcSubjectB(""); setEditingBid(null); setOptimizeTime(false); setLinkA(""); setLinkB("");
      getBroadcasts().then((d) => setBroadcasts(d.broadcasts || [])).catch(() => {});
    } catch {
      toast.error("Could not send the broadcast.");
    } finally {
      setSendingBc(false);
    }
  };

  const editScheduled = (b) => {
    setEditingBid(b.bid);
    setBcSubject(b.subject || "");
    setBcMessage(b.message || "");
    setBcSegment(b.segment || "all");
    setBcSchedule(b.scheduled_for ? new Date(b.scheduled_for).toISOString().slice(0, 16) : "");
    toast("Editing scheduled broadcast — update and save.");
  };

  const saveRec = async () => {
    setSavingRec(true);
    try {
      const d = await saveRecurring({
        active: !!recurring?.active,
        mode: recurring?.mode || "ai",
        subject: recurring?.subject || "",
        message: recurring?.message || "",
        segment: recurring?.segment || "all",
        next_run: recurring?.next_run ? new Date(recurring.next_run).toISOString() : "",
      });
      setRecurring(d.recurring || null);
      toast.success("Automation saved.");
    } catch {
      toast.error("Could not save automation.");
    } finally {
      setSavingRec(false);
    }
  };

  const draftBc = async () => {
    if (draftingBc) return;
    setDraftingBc(true);
    try {
      const res = await draftBroadcast();
      setBcSubject(res.subject || "");
      setBcMessage(res.message || "");
      toast.success("Draft ready — review and edit before sending.");
    } catch {
      toast.error("Could not draft a tip right now.");
    } finally {
      setDraftingBc(false);
    }
  };

  const cancelBc = async (bid) => {
    try {
      await cancelBroadcast(bid);
      toast.success("Scheduled broadcast cancelled.");
      getBroadcasts().then((d) => setBroadcasts(d.broadcasts || [])).catch(() => {});
    } catch {
      toast.error("Could not cancel.");
    }
  };

  const resendConfirm = async (email) => {
    setResendingEmail(email);
    try {
      const res = await resendConfirmation(email);
      toast[res.sent ? "success" : "error"](res.sent ? "Confirmation email resent." : "Could not resend.");
    } catch {
      toast.error("Could not resend.");
    } finally {
      setResendingEmail("");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      await adminLogin(loginEmail.trim(), loginPassword);
      setAuthed(true);
      setLoginPassword("");
    } catch (err) {
      console.error("admin login failed", err);
      const d = err?.response?.data?.detail;
      setLoginError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    adminLogout();
    setData(null);
    setAuthed(false);
  };
  const saveHour = async (val) => {
    setSavingHour(true);
    try {
      let payload;
      if (val === "") {
        payload = { digest_hour_local: null, digest_utc_hour: null, digest_tz_label: null };
      } else {
        const localHour = parseInt(val, 10);
        const offset = new Date().getTimezoneOffset(); // UTC = local + offset (minutes)
        const utcHour = (((((localHour * 60 + offset) % 1440) + 1440) % 1440) / 60) | 0;
        payload = { digest_hour_local: localHour, digest_utc_hour: utcHour, digest_tz_label: TZ };
      }
      const res = await saveAdminSettings(payload);
      setSettings(res);
      toast.success(
        val === ""
          ? "Digest set to send at the first check each day."
          : `Digest will arrive around ${hourLabel(parseInt(val, 10))}.`
      );
    } catch (e) {
      toast.error("Could not save. Please try again.");
    } finally {
      setSavingHour(false);
    }
  };

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const res = await sendTestDigest();
      if (res && res.sent) {
        toast.success(
          res.preview
            ? "Sent a sample digest to the admin email — check your inbox."
            : `Sent a digest of ${res.count} overdue review(s) to the admin email.`
        );
      } else {
        toast.error("Could not send the test digest. Check the email setup.");
      }
    } catch (e) {
      toast.error("Could not send the test digest.");
    } finally {
      setSendingTest(false);
    }
  };

  const saveGoal = async () => {
    setSavingGoal(true);
    try {
      const g = goalInput === "" ? null : Math.max(0, parseInt(goalInput, 10) || 0);
      const res = await saveAdminSettings({ revenue_goal: g });
      setData((prev) => ({ ...prev, settings: res }));
      setEditingGoal(false);
      toast.success(g ? `Monthly goal set to $${g}.` : "Goal cleared.");
    } catch (e) {
      toast.error("Could not save the goal.");
    } finally {
      setSavingGoal(false);
    }
  };

  const pickReviewPhoto = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const next = [...reviewPhotos];
    for (const f of files) {
      if (next.length >= 3) { toast.error("Up to 3 photos."); break; }
      if (!/^image\//.test(f.type)) { toast.error("Images only."); continue; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} is too large (max 10 MB).`); continue; }
      next.push(f);
    }
    setReviewPhotos(next);
    if (reviewFileRef.current) reviewFileRef.current.value = "";
  };

  const markReviewed = async (requestId) => {
    setReviewing((prev) => ({ ...prev, [requestId]: true }));
    try {
      let attachments = [];
      if (reviewPhotos.length) attachments = await Promise.all(reviewPhotos.map((f) => uploadPhoto(f)));
      const res = await updateDoctorStatus(requestId, "reviewed", reviewNote, attachments);
      const entry = { role: "physician", text: reviewNote.trim() };
      if (attachments.length) entry.attachments = attachments;
      setData((prev) => ({
        ...prev,
        doctor_open_count: Math.max(0, (prev.doctor_open_count ?? 0) - 1),
        doctor_reviewed_count: (prev.doctor_reviewed_count ?? 0) + 1,
        doctor_requests: prev.doctor_requests.map((r) =>
          r.request_id === requestId
            ? { ...r, status: "reviewed", review_note: reviewNote.trim(),
                thread: [...(r.thread || []), entry] }
            : r
        ),
      }));
      setReviewFormId(null);
      setReviewNote("");
      setReviewPhotos([]);
      if (reviewFileRef.current) reviewFileRef.current.value = "";
      toast.success(
        res && res.email_sent
          ? "Marked reviewed — the visitor was emailed your note."
          : "Marked reviewed. No email on file, so no note was sent."
      );
    } catch (e) {
      toast.error("Could not mark reviewed. Please try again.");
    } finally {
      setReviewing((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const refund = async (requestId) => {
    setRefunding((prev) => ({ ...prev, [requestId]: true }));
    try {
      const res = await refundDoctorRequest(requestId, refundReason, refundNotify);
      setData((prev) => ({
        ...prev,
        doctor_paid_count: Math.max(0, (prev.doctor_paid_count ?? 0) - 1),
        doctor_refunded_count: (prev.doctor_refunded_count ?? 0) + 1,
        doctor_requests: prev.doctor_requests.map((r) =>
          r.request_id === requestId
            ? { ...r, status: "refunded", refunded: true, refund_reason: refundReason.trim() }
            : r
        ),
      }));
      setRefundFormId(null);
      setRefundReason("");
      toast.success(
        res && res.email_sent
          ? "Refund issued — a confirmation email was sent to the visitor."
          : refundNotify
          ? "Refund issued. No email on file, so no confirmation was sent."
          : "Refund issued. Visitor was not notified."
      );
    } catch (e) {
      toast.error("Refund failed. Please try again.");
    } finally {
      setRefunding((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const addTopic = async (question) => {
    setAddedTopics((prev) => ({ ...prev, [question]: "loading" }));
    try {
      const { topic } = await addLibraryTopic(question);
      setAddedTopics((prev) => ({ ...prev, [question]: topic.slug }));
    } catch (e) {
      setAddedTopics((prev) => {
        const next = { ...prev };
        delete next[question];
        return next;
      });
    }
  };

  if (authed === null) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-20 text-center" data-testid="admin-checking">
        <Loader2 className="w-10 h-10 text-teal-600 animate-spin mx-auto" />
      </div>
    );
  }

  if (authed === false) {
    return (
      <div className="max-w-md mx-auto px-4 py-16" data-testid="admin-login">
        <div className="rounded-2xl border-2 border-slate-200 bg-white p-8 shadow-sm">
          <div className="grid place-items-center w-14 h-14 rounded-2xl bg-teal-100 text-teal-700 mx-auto mb-4">
            <Lock className="w-7 h-7" />
          </div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900 text-center mb-1">
            Admin Sign In
          </h1>
          <p className="text-sm text-slate-500 text-center mb-6">
            This dashboard is restricted to authorized staff.
          </p>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="email"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              placeholder="Admin email"
              autoComplete="username"
              required
              data-testid="admin-login-email"
              className="w-full bg-white border-2 border-slate-200 rounded-xl px-4 py-3 text-base text-slate-800 focus:border-teal-400 outline-none min-h-[52px]"
            />
            <input
              type="password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              placeholder="Password"
              autoComplete="current-password"
              required
              data-testid="admin-login-password"
              className="w-full bg-white border-2 border-slate-200 rounded-xl px-4 py-3 text-base text-slate-800 focus:border-teal-400 outline-none min-h-[52px]"
            />
            {loginError && (
              <p className="text-sm font-semibold text-red-600" data-testid="admin-login-error">{loginError}</p>
            )}
            <button
              type="submit"
              disabled={loggingIn}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white font-bold text-lg px-6 py-3 rounded-xl transition-transform active:scale-95 min-h-[52px]"
            >
              {loggingIn ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-5 h-5" />}
              {loggingIn ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-20 text-center">
        <Loader2 className="w-10 h-10 text-teal-600 animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-dashboard">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 mb-1">
            Administrator Dashboard
          </h1>
          <p className="text-lg text-slate-500">
            Usage analytics. No personally identifying information is stored.
          </p>
        </div>
        <button
          onClick={handleLogout}
          data-testid="admin-logout"
          className="inline-flex items-center gap-2 shrink-0 bg-white border-2 border-slate-300 text-slate-700 hover:bg-slate-50 font-semibold text-sm px-4 py-2 rounded-xl transition-colors"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>

      <div className="rounded-2xl border-2 border-slate-200 bg-white p-5 mb-8" data-testid="admin-audit">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900">
            <ScrollText className="w-5 h-5 text-teal-600" /> Admin Activity Log
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={exportAudit}
              disabled={exportingAudit}
              data-testid="audit-export-csv"
              className="inline-flex items-center gap-1.5 border-2 border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-60 font-semibold text-sm px-3 py-1.5 rounded-lg transition-colors"
            >
              {exportingAudit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export CSV
            </button>
            <button
              onClick={handleClearLog}
              disabled={clearingLog || audit.length === 0}
              data-testid="audit-clear-log"
              className="inline-flex items-center gap-1.5 border-2 border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 font-semibold text-sm px-3 py-1.5 rounded-lg transition-colors"
            >
              {clearingLog ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Clear log
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3 mb-4 pb-4 border-b border-slate-100">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Action</label>
            <select
              value={auditAction}
              onChange={(e) => setAuditAction(e.target.value)}
              data-testid="audit-filter-action"
              className="bg-white border-2 border-slate-200 focus:border-teal-400 rounded-lg px-3 py-1.5 text-sm font-medium text-slate-800 outline-none"
            >
              <option value="">All actions</option>
              <option value="refund">Refund</option>
              <option value="doctor_request_status">Review status</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">From</label>
            <input
              type="date"
              value={auditStart}
              onChange={(e) => setAuditStart(e.target.value)}
              data-testid="audit-filter-start"
              className="bg-white border-2 border-slate-200 focus:border-teal-400 rounded-lg px-3 py-1.5 text-sm text-slate-800 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">To</label>
            <input
              type="date"
              value={auditEnd}
              onChange={(e) => setAuditEnd(e.target.value)}
              data-testid="audit-filter-end"
              className="bg-white border-2 border-slate-200 focus:border-teal-400 rounded-lg px-3 py-1.5 text-sm text-slate-800 outline-none"
            />
          </div>
          <button
            onClick={loadAudit}
            data-testid="audit-apply-filters"
            className="inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm px-3 py-1.5 rounded-lg transition-colors"
          >
            <Filter className="w-4 h-4" /> Apply
          </button>
          {(auditAction || auditStart || auditEnd) && (
            <button
              onClick={clearAuditFilters}
              data-testid="audit-clear-filters"
              className="text-sm font-semibold text-slate-500 hover:text-slate-800 px-2 py-1.5"
            >
              Clear
            </button>
          )}
        </div>
        {audit.length === 0 ? (
          <p className="text-sm text-slate-500">No admin actions recorded yet.</p>
        ) : (
          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
            {audit.map((a, i) => (
              <div key={`${a.at}-${i}`} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 py-2 text-sm" data-testid={`audit-row-${i}`}>
                <span className="font-mono text-xs text-slate-400">{new Date(a.at).toLocaleString()}</span>
                <span className="font-semibold text-slate-800">{a.admin_email}</span>
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-bold ${a.action === "refund" ? "bg-red-100 text-red-700" : "bg-sky-100 text-sky-700"}`}>
                  {a.action}
                </span>
                {a.target && <span className="text-slate-500">#{String(a.target).slice(0, 8)}</span>}
                {a.meta?.status && <span className="text-slate-500">→ {a.meta.status}</span>}
                {a.meta?.amount != null && <span className="text-slate-500">${a.meta.amount}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl border-2 border-slate-200 bg-white p-5 mb-8" data-testid="admin-subscribers">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900">
            <Mail className="w-5 h-5 text-teal-600" /> Subscribers
            <span className="ml-1 bg-teal-100 text-teal-800 font-bold text-sm px-2.5 py-0.5 rounded-full" data-testid="subscribers-count">{subscribers.length}</span>
          </h2>
          <button
            onClick={exportSubs}
            disabled={exportingSubs || subscribers.length === 0}
            data-testid="subscribers-export-csv"
            className="inline-flex items-center gap-1.5 border-2 border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-60 font-semibold text-sm px-3 py-1.5 rounded-lg transition-colors"
          >
            {exportingSubs ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Export CSV
          </button>
        </div>
        <p className="text-sm text-slate-500 mb-3">People who opted in to occasional health tips when emailing themselves a result.</p>

        {milestoneReached && (
          <div className="flex items-center gap-3 rounded-xl border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 px-4 py-3 mb-4" data-testid="growth-milestone-banner">
            <PartyPopper className="w-6 h-6 text-amber-500 shrink-0" />
            <div>
              <p className="font-heading font-bold text-slate-900 text-sm">{milestoneReached.toLocaleString()}+ confirmed subscribers! 🎉</p>
              <p className="text-xs text-slate-600">You now have {confirmedCount.toLocaleString()} confirmed subscribers on your health-tips list.</p>
            </div>
          </div>
        )}

        {growthData.length >= 2 && (
          <div className="rounded-xl border-2 border-emerald-100 bg-emerald-50/40 p-4 mb-4" data-testid="subscriber-growth-card">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-emerald-600" />
              <h3 className="font-heading font-bold text-slate-900 text-sm">Confirmed subscribers over time</h3>
              <span className="ml-auto text-xs font-bold text-emerald-700">{growthData[growthData.length - 1].total} total</span>
            </div>
            <ResponsiveContainer width="100%" height={140}>
              <AreaChart data={growthData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="growthFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} width={28} />
                <Tooltip />
                <Area type="monotone" dataKey="total" stroke="#10b981" strokeWidth={2} fill="url(#growthFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="rounded-xl border-2 border-teal-100 bg-teal-50/40 p-4 mb-4" data-testid="broadcast-compose">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading font-bold text-slate-900">Send a broadcast</h3>
              {bestHourLabel && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 font-semibold text-[11px] px-2 py-0.5" data-testid="best-time-suggestion" title="Your subscribers open most around this hour">
                  <Zap className="w-3 h-3" /> Best open time: {bestHourLabel}{bestDowLabel ? ` · ${bestDowLabel}s` : ""}
                </span>
              )}
            </div>
            <button
              onClick={draftBc}
              disabled={draftingBc}
              data-testid="broadcast-draft"
              className="inline-flex items-center gap-1.5 border-2 border-teal-300 text-teal-700 hover:bg-teal-50 disabled:opacity-60 font-semibold text-xs px-2.5 py-1.5 rounded-lg transition-colors"
            >
              {draftingBc ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {draftingBc ? "Drafting…" : "Draft with AI"}
            </button>
          </div>
          <input
            type="text"
            value={bcSubject}
            onChange={(e) => setBcSubject(e.target.value)}
            placeholder="Subject — e.g. This month's health tip"
            data-testid="broadcast-subject"
            className="w-full bg-white border-2 border-slate-200 focus:border-teal-400 rounded-xl px-3.5 py-2.5 text-slate-900 outline-none transition-colors mb-2"
          />
          <textarea
            value={bcMessage}
            onChange={(e) => setBcMessage(e.target.value)}
            placeholder="Write your message… (an unsubscribe link is added automatically)"
            data-testid="broadcast-message"
            rows={4}
            className="w-full bg-white border-2 border-slate-200 focus:border-teal-400 rounded-xl px-3.5 py-2.5 text-slate-900 outline-none transition-colors resize-y"
          />
          {(bcSubject.trim() || bcMessage.trim()) && (
            <div className="mt-3 rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="broadcast-subject-preview">
              <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wide text-slate-400">Inbox preview</div>
              <div className="flex items-start gap-3 px-3.5 py-2.5">
                <span className="grid place-items-center w-9 h-9 rounded-full bg-teal-100 text-teal-700 font-bold text-sm shrink-0">A</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-slate-900 text-sm truncate">AskMyDoctor AI</span>
                    <span className="text-[11px] text-slate-400 shrink-0">now</span>
                  </div>
                  <div className="text-sm text-slate-800 font-medium truncate" data-testid="preview-subject-line">{bcSubject.trim() || "(no subject)"}</div>
                  <div className="text-xs text-slate-500 truncate" data-testid="preview-snippet-line">{(bcMessage.split("\n").find((l) => l.trim()) || "").trim() || "Your message preview appears here…"}</div>
                </div>
              </div>
            </div>
          )}
          {!editingBid && !bcSchedule && !linkA.trim() && !linkB.trim() && (
            <input
              type="text"
              value={bcSubjectB}
              onChange={(e) => setBcSubjectB(e.target.value)}
              placeholder="Subject B — optional A/B test (winner auto-sends to the rest)"
              data-testid="broadcast-subject-b"
              className="w-full mt-2 bg-white border-2 border-dashed border-slate-300 focus:border-teal-400 rounded-xl px-3.5 py-2.5 text-slate-900 outline-none transition-colors"
            />
          )}
          {!editingBid && !bcSchedule && !bcSubjectB.trim() && (
            <div className="mt-2 rounded-xl border-2 border-dashed border-sky-300 bg-sky-50/40 p-3" data-testid="broadcast-link-ab">
              <div className="flex items-center gap-1.5 mb-2 text-xs font-bold text-sky-700">
                <Link2 className="w-3.5 h-3.5" /> Link A/B test (optional)
                <button
                  type="button"
                  onClick={() => setBcMessage((m) => (m.includes("{{link}}") ? m : (m ? m + " {{link}}" : "{{link}}")))}
                  data-testid="broadcast-link-insert"
                  className="ml-auto text-[11px] font-semibold text-sky-600 hover:text-sky-800 underline"
                >
                  Insert {"{{link}}"} in message
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input type="url" value={linkA} onChange={(e) => setLinkA(e.target.value)} placeholder="Link A — https://…" data-testid="broadcast-link-a" className="bg-white border-2 border-slate-200 focus:border-sky-400 rounded-lg px-3 py-2 text-sm text-slate-900 outline-none" />
                <input type="url" value={linkB} onChange={(e) => setLinkB(e.target.value)} placeholder="Link B — https://…" data-testid="broadcast-link-b" className="bg-white border-2 border-slate-200 focus:border-sky-400 rounded-lg px-3 py-2 text-sm text-slate-900 outline-none" />
              </div>
              {linkA.trim() && linkB.trim() && (
                <p className="text-[11px] text-sky-700 mt-1.5">A test slice gets each link; the one with more clicks auto-sends to the rest. Put {"{{link}}"} where the link should appear.</p>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Schedule (optional)
            </label>
            <input
              type="datetime-local"
              value={bcSchedule}
              onChange={(e) => setBcSchedule(e.target.value)}
              data-testid="broadcast-schedule"
              className="bg-white border-2 border-slate-200 focus:border-teal-400 rounded-lg px-2.5 py-1.5 text-sm text-slate-900 outline-none"
            />
            {bcSchedule && (
              <button onClick={() => setBcSchedule("")} className="text-xs text-slate-500 hover:text-red-600 font-semibold">Clear</button>
            )}
            <label className="text-xs font-semibold text-slate-600 ml-2">Send to</label>
            <select
              value={bcSegment}
              onChange={(e) => setBcSegment(e.target.value)}
              data-testid="broadcast-segment"
              className="bg-white border-2 border-slate-200 focus:border-teal-400 rounded-lg px-2.5 py-1.5 text-sm text-slate-900 outline-none"
            >
              <option value="all">All confirmed</option>
              {segments.map((sg) => (
                <option key={sg} value={sg}>Used: {sg}</option>
              ))}
            </select>
            {!editingBid && !bcSchedule && !bcSubjectB.trim() && !(linkA.trim() && linkB.trim()) && (
              <label className="inline-flex items-center gap-1.5 ml-2 cursor-pointer select-none text-xs font-semibold text-slate-600" data-testid="broadcast-optimize-label">
                <input type="checkbox" checked={optimizeTime} onChange={(e) => setOptimizeTime(e.target.checked)} data-testid="broadcast-optimize" className="w-4 h-4 accent-teal-600" />
                <Zap className="w-3.5 h-3.5 text-amber-500" /> Send at best time
              </label>
            )}
          </div>
          <div className="flex items-center gap-3 mt-3">
            <button
              onClick={() => (editingBid ? sendBc() : setBcConfirmOpen(true))}
              disabled={sendingBc || !bcSubject.trim() || !bcMessage.trim() || (!editingBid && segmentCount === 0)}
              data-testid="broadcast-send"
              className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white font-bold text-sm px-4 py-2 rounded-xl transition-transform active:scale-95"
            >
              {sendingBc ? <Loader2 className="w-4 h-4 animate-spin" /> : (bcSchedule || editingBid) ? <Clock className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
              {sendingBc ? "Saving…" : editingBid ? "Update scheduled" : bcSchedule ? "Schedule broadcast" : "Send broadcast"}
            </button>
            {editingBid && (
              <button onClick={() => { setEditingBid(null); setBcSubject(""); setBcMessage(""); setBcSchedule(""); setBcSegment("all"); }} data-testid="broadcast-edit-cancel" className="text-sm font-semibold text-slate-500 hover:text-red-600">Cancel edit</button>
            )}
            {!editingBid && (
              <button
                onClick={testBc}
                disabled={testingBc || !bcSubject.trim() || !bcMessage.trim()}
                data-testid="broadcast-test"
                className="inline-flex items-center gap-1.5 border-2 border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-60 font-semibold text-sm px-3 py-2 rounded-xl transition-colors"
              >
                {testingBc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send test to me
              </button>
            )}
            {!editingBid && (
              <button
                onClick={runDemo}
                disabled={runningDemo}
                data-testid="broadcast-ab-demo"
                title="Emails Subject A + B to you, resolves a winner, then sends the winner alert — the full A/B flow, live."
                className="inline-flex items-center gap-1.5 border-2 border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-60 font-semibold text-sm px-3 py-2 rounded-xl transition-colors"
              >
                {runningDemo ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />} Run A/B demo to me
              </button>
            )}
          </div>
          {optimizeTime && !editingBid && !bcSchedule && !bcSubjectB.trim() && (
            <p className="text-xs text-amber-700 mt-1.5 flex items-center gap-1.5" data-testid="broadcast-optimize-hint">
              <Zap className="w-3.5 h-3.5" /> {openHours?.best_hour_utc != null ? `Will schedule for your best open hour (${bestHourLabel} your time).` : "No open history yet — will schedule for the start of your daytime window."}
            </p>
          )}
          <p className="text-xs text-slate-500 mt-2">{editingBid ? "Editing a scheduled broadcast." : `${segmentCount} subscriber(s)${bcSegment !== "all" ? ` who used ${bcSegment}` : ""} will receive this.`}</p>
        </div>

        <div className="rounded-xl border-2 border-indigo-100 bg-indigo-50/40 p-4 mb-4" data-testid="quiet-hours-card">
          <div className="flex items-center gap-2 mb-1">
            <MoonStar className="w-4 h-4 text-indigo-600" />
            <h3 className="font-heading font-bold text-slate-900">Quiet hours</h3>
          </div>
          <p className="text-sm text-slate-500 mb-3">Sends that land outside this window are auto-moved to the next start time — so nothing goes out overnight.</p>
          {quietCfg && (
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-xs font-semibold text-slate-600">
                From
                <select value={quietCfg.start} onChange={(e) => setQuietCfg({ ...quietCfg, start: Number(e.target.value) })} data-testid="quiet-start" className="block mt-1 bg-white border-2 border-slate-200 focus:border-indigo-400 rounded-lg px-2.5 py-1.5 text-sm text-slate-900 outline-none">
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>{new Date(Date.UTC(2020, 0, 1, h)).toLocaleTimeString([], { hour: "numeric", timeZone: "UTC" })}</option>
                  ))}
                </select>
              </label>
              <label className="text-xs font-semibold text-slate-600">
                To
                <select value={quietCfg.end} onChange={(e) => setQuietCfg({ ...quietCfg, end: Number(e.target.value) })} data-testid="quiet-end" className="block mt-1 bg-white border-2 border-slate-200 focus:border-indigo-400 rounded-lg px-2.5 py-1.5 text-sm text-slate-900 outline-none">
                  {Array.from({ length: 24 }, (_, h) => h + 1).map((h) => (
                    <option key={h} value={h}>{h === 24 ? "12 AM" : new Date(Date.UTC(2020, 0, 1, h)).toLocaleTimeString([], { hour: "numeric", timeZone: "UTC" })}</option>
                  ))}
                </select>
              </label>
              <label className="text-xs font-semibold text-slate-600">
                Timezone
                <select value={quietCfg.tz} onChange={(e) => setQuietCfg({ ...quietCfg, tz: e.target.value })} data-testid="quiet-tz" className="block mt-1 bg-white border-2 border-slate-200 focus:border-indigo-400 rounded-lg px-2.5 py-1.5 text-sm text-slate-900 outline-none">
                  {TZ_OPTIONS.map((t) => (<option key={t.v} value={t.v}>{t.l}</option>))}
                </select>
              </label>
              <button onClick={saveQuiet} disabled={savingQuiet} data-testid="quiet-save" className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-bold text-sm px-4 py-2 rounded-xl transition-transform active:scale-95">
                {savingQuiet ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
              </button>
            </div>
          )}
        </div>

        <div className="rounded-xl border-2 border-sky-100 bg-sky-50/40 p-4 mb-4" data-testid="weekly-digest-card">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2">
              <Send className="w-4 h-4 text-sky-600" />
              <h3 className="font-heading font-bold text-slate-900">Weekly digest</h3>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={!!digestCfg?.active} onChange={(e) => toggleDigest(e.target.checked)} data-testid="weekly-digest-toggle" className="w-4 h-4 accent-sky-600" />
              <span className="text-sm font-semibold text-slate-700">On</span>
            </label>
          </div>
          <p className="text-sm text-slate-500 mb-3">A summary of the week's opens, clicks and top links, emailed to you every Monday ~9 AM ET.</p>
          <div className="flex items-center gap-3">
            <button onClick={sendDigestNow} disabled={digestSending} data-testid="weekly-digest-send" className="inline-flex items-center gap-1.5 border-2 border-sky-300 text-sky-700 hover:bg-sky-50 disabled:opacity-60 font-semibold text-sm px-3 py-2 rounded-xl transition-colors">
              {digestSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send digest now
            </button>
            <button onClick={previewDigest} disabled={previewingDigest} data-testid="weekly-digest-preview" className="inline-flex items-center gap-1.5 border-2 border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-60 font-semibold text-sm px-3 py-2 rounded-xl transition-colors">
              {previewingDigest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Preview
            </button>
            {digestCfg?.next_run && digestCfg?.active && (
              <span className="text-xs text-slate-500">Next: {new Date(digestCfg.next_run).toLocaleString()}</span>
            )}
          </div>
        </div>

        <div className="rounded-xl border-2 border-violet-100 bg-violet-50/40 p-4 mb-4" data-testid="recurring-card">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><Clock className="w-4 h-4 text-violet-600" /> Recurring monthly tip</h3>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={!!recurring?.active} onChange={(e) => setRecurring({ ...(recurring || {}), active: e.target.checked })} data-testid="recurring-active" className="w-4 h-4 accent-violet-600" />
              <span className="text-sm font-semibold text-slate-700">Active</span>
            </label>
          </div>
          <p className="text-xs text-slate-500 mb-3">Automatically emails your confirmed subscribers once a month.</p>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <label className="text-xs font-semibold text-slate-600">Content</label>
            <select value={recurring?.mode || "ai"} onChange={(e) => setRecurring({ ...(recurring || {}), mode: e.target.value })} data-testid="recurring-mode" className="bg-white border-2 border-slate-200 rounded-lg px-2.5 py-1.5 text-sm outline-none">
              <option value="ai">AI-generated fresh each month</option>
              <option value="fixed">Fixed subject & message</option>
            </select>
            <label className="text-xs font-semibold text-slate-600 ml-2">Send to</label>
            <select value={recurring?.segment || "all"} onChange={(e) => setRecurring({ ...(recurring || {}), segment: e.target.value })} data-testid="recurring-segment" className="bg-white border-2 border-slate-200 rounded-lg px-2.5 py-1.5 text-sm outline-none">
              <option value="all">All confirmed</option>
              {segments.map((sg) => <option key={sg} value={sg}>Used: {sg}</option>)}
            </select>
            <label className="text-xs font-semibold text-slate-600 ml-2">Next run</label>
            <input type="datetime-local" value={recurring?.next_run ? new Date(recurring.next_run).toISOString().slice(0, 16) : ""} onChange={(e) => setRecurring({ ...(recurring || {}), next_run: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="recurring-next" className="bg-white border-2 border-slate-200 rounded-lg px-2.5 py-1.5 text-sm outline-none" />
          </div>
          {recurring?.mode === "fixed" && (
            <div className="space-y-2 mb-2">
              <input type="text" value={recurring?.subject || ""} onChange={(e) => setRecurring({ ...(recurring || {}), subject: e.target.value })} placeholder="Subject" data-testid="recurring-subject" className="w-full bg-white border-2 border-slate-200 rounded-lg px-3 py-2 text-sm outline-none" />
              <textarea value={recurring?.message || ""} onChange={(e) => setRecurring({ ...(recurring || {}), message: e.target.value })} placeholder="Message" data-testid="recurring-message" rows={3} className="w-full bg-white border-2 border-slate-200 rounded-lg px-3 py-2 text-sm outline-none resize-y" />
            </div>
          )}
          <button onClick={saveRec} disabled={savingRec} data-testid="recurring-save" className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white font-bold text-sm px-4 py-2 rounded-xl transition-transform active:scale-95">
            {savingRec ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save automation
          </button>
          {recurring?.last_run && <p className="text-xs text-slate-500 mt-2">Last sent {new Date(recurring.last_run).toLocaleString()}.</p>}
        </div>

        {broadcasts.length > 0 && (
          <div className="mb-4" data-testid="broadcast-history">
            <h3 className="font-heading font-bold text-slate-900 mb-2 text-sm uppercase tracking-wide text-slate-500">Past & scheduled broadcasts</h3>
            <div className="max-h-52 overflow-y-auto divide-y divide-slate-100 rounded-xl border border-slate-200">
              {broadcasts.map((b, i) => (
                <div key={b.bid || i} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-3.5 py-2.5 text-sm hover:bg-slate-50 cursor-pointer" data-testid={`broadcast-row-${i}`} onClick={() => setBcDetail(b)}>
                  <span className="font-semibold text-slate-800">{b.subject}</span>
                  {b.status === "scheduled" ? (
                    <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-bold bg-amber-100 text-amber-800">scheduled {b.scheduled_for ? new Date(b.scheduled_for).toLocaleString() : ""}</span>
                  ) : (
                    <>
                      <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-bold bg-teal-100 text-teal-800">{b.sent}/{b.recipients} sent</span>
                      <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-bold bg-sky-100 text-sky-800" data-testid={`broadcast-stats-${i}`}>{b.opens || 0} opens · {b.clicks || 0} clicks</span>
                    </>
                  )}
                  {b.segment && b.segment !== "all" && <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-bold bg-slate-100 text-slate-600">{b.segment}</span>}
                  {b.status === "scheduled" && (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); editScheduled(b); }}
                        data-testid={`broadcast-edit-${i}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:text-teal-800"
                      >
                        Edit
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); cancelBc(b.bid); }}
                        data-testid={`broadcast-cancel-${i}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-red-600"
                      >
                        <X className="w-3.5 h-3.5" /> Cancel
                      </button>
                    </>
                  )}
                  <span className="font-mono text-xs text-slate-400 ml-auto">{b.created_at ? new Date(b.created_at).toLocaleString() : ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {rateData.length > 0 && (
          <div className="mb-4 rounded-xl border-2 border-sky-100 bg-sky-50/40 p-4" data-testid="open-rate-trend">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <h3 className="font-heading font-bold text-slate-900 text-sm uppercase tracking-wide text-slate-500">Open & click rate</h3>
              {bestHourLabel && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs font-semibold text-sky-800 bg-sky-100 rounded-full px-2.5 py-1" data-testid="best-send-time">
                    Best send time ≈ {bestHourLabel} ({openHours.total} opens)
                  </span>
                  {bestDowLabel && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-800 bg-indigo-100 rounded-full px-2.5 py-1" data-testid="best-send-day">
                      <CalendarDays className="w-3 h-3" /> Best day: {bestDowLabel}
                    </span>
                  )}
                </div>
              )}
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <ComposedChart data={rateData} margin={{ left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} unit="%" />
                <Tooltip formatter={(v) => `${v}%`} />
                <Legend />
                <Line type="monotone" dataKey="open" name="Open %" stroke="#0ea5e9" strokeWidth={2} />
                <Line type="monotone" dataKey="click" name="Click %" stroke="#0d9488" strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        {subscribers.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="subscribers-empty">No subscribers yet.</p>
        ) : (
          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
            {subscribers.map((s, i) => (
              <div key={s.email || i} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 py-2 text-sm" data-testid={`subscriber-row-${i}`}>
                <span className="font-semibold text-slate-800">{s.email}</span>
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-bold ${s.confirmed ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid={`subscriber-status-${i}`}>
                  {s.confirmed ? "Confirmed" : "Pending"}
                </span>
                {s.source && <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-bold bg-slate-100 text-slate-600">{s.source}</span>}
                {!s.confirmed && !s.unsubscribed && (
                  <button
                    onClick={() => resendConfirm(s.email)}
                    disabled={resendingEmail === s.email}
                    data-testid={`subscriber-resend-${i}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:text-teal-800 disabled:opacity-60"
                  >
                    {resendingEmail === s.email ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Resend
                  </button>
                )}
                <span className="font-mono text-xs text-slate-400 ml-auto">{s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString() : ""}</span>
              </div>
            ))}
          </div>
        )}

        <AlertDialog open={bcConfirmOpen} onOpenChange={setBcConfirmOpen}>
          <AlertDialogContent data-testid="broadcast-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{bcSchedule ? "Schedule this broadcast?" : "Send this broadcast?"}</AlertDialogTitle>
              <AlertDialogDescription>
                "{bcSubject.trim() || "(no subject)"}" will {bcSchedule ? `be sent on ${new Date(bcSchedule).toLocaleString()} to` : "be emailed to"} {segmentCount} subscriber(s){bcSegment !== "all" ? ` who used ${bcSegment}` : ""}. This can't be undone once sent.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="broadcast-confirm-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={sendBc} data-testid="broadcast-confirm-send" className="bg-teal-600 hover:bg-teal-700">{bcSchedule ? "Schedule" : "Send now"}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog open={!!bcDetail} onOpenChange={(o) => !o && setBcDetail(null)}>
          <DialogContent data-testid="broadcast-detail-dialog">
            <DialogHeader>
              <DialogTitle>{bcDetail?.subject}</DialogTitle>
              <DialogDescription>Full message and delivery breakdown for this broadcast.</DialogDescription>
            </DialogHeader>
            {bcDetail && (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className={`inline-flex rounded-full px-2.5 py-1 font-bold ${bcDetail.status === "scheduled" ? "bg-amber-100 text-amber-800" : "bg-teal-100 text-teal-800"}`}>{bcDetail.status || "sent"}</span>
                  {bcDetail.status === "scheduled" ? (
                    <span className="inline-flex rounded-full px-2.5 py-1 font-bold bg-slate-100 text-slate-600">scheduled for {bcDetail.scheduled_for ? new Date(bcDetail.scheduled_for).toLocaleString() : ""}</span>
                  ) : (
                    <span className="inline-flex rounded-full px-2.5 py-1 font-bold bg-slate-100 text-slate-600">delivered to {bcDetail.sent}/{bcDetail.recipients}</span>
                  )}
                  {bcDetail.sent_at && <span className="inline-flex rounded-full px-2.5 py-1 font-bold bg-slate-100 text-slate-600">sent {new Date(bcDetail.sent_at).toLocaleString()}</span>}
                  {bcDetail.status !== "scheduled" && <span className="inline-flex rounded-full px-2.5 py-1 font-bold bg-sky-100 text-sky-800" data-testid="broadcast-detail-stats">{bcDetail.opens || 0} opens · {bcDetail.clicks || 0} clicks</span>}
                  {bcDetail.segment && bcDetail.segment !== "all" && <span className="inline-flex rounded-full px-2.5 py-1 font-bold bg-slate-100 text-slate-600">segment: {bcDetail.segment}</span>}
                </div>
                <div className="whitespace-pre-wrap text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-xl p-4 max-h-72 overflow-y-auto" data-testid="broadcast-detail-message">{bcDetail.message}</div>
                {bcDetail.winner && bcDetail.ab_kind !== "link" && (
                  <div className="flex items-center gap-2 text-xs" data-testid="broadcast-detail-winner">
                    <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-800 font-bold px-2.5 py-1 rounded-full">
                      <Trophy className="w-3.5 h-3.5" /> Subject {bcDetail.winner === "b" ? "B" : "A"} won
                    </span>
                    <span className="text-slate-500">A: {bcDetail.opens_a ?? 0} opens · B: {bcDetail.opens_b ?? 0} opens</span>
                  </div>
                )}
                {bcDetail.ab_kind === "link" && (
                  <div data-testid="broadcast-detail-link-ab">
                    <div className="flex items-center gap-2 text-xs mb-2">
                      <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-800 font-bold px-2.5 py-1 rounded-full">
                        <Trophy className="w-3.5 h-3.5" /> Link {bcDetail.winner === "b" ? "B" : "A"} won
                      </span>
                      {bcDetail.winner_link && <span className="text-slate-500 truncate">winning URL: <a href={bcDetail.winner_link} target="_blank" rel="noreferrer" className="text-teal-700 hover:underline">{bcDetail.winner_link}</a></span>}
                    </div>
                    <div className="rounded-xl border border-slate-200 overflow-hidden divide-y divide-slate-100">
                      <div className={`flex items-center justify-between gap-3 px-3 py-2 text-sm ${bcDetail.winner === "a" ? "bg-green-50" : ""}`} data-testid="link-ab-a">
                        <span className="truncate"><span className="font-bold text-slate-700 mr-1">A</span><a href={bcDetail.link_a} target="_blank" rel="noreferrer" className="text-teal-700 hover:underline">{bcDetail.link_a}</a></span>
                        <span className="shrink-0 font-bold text-slate-900">{bcDetail.clicks_a ?? 0} clicks</span>
                      </div>
                      <div className={`flex items-center justify-between gap-3 px-3 py-2 text-sm ${bcDetail.winner === "b" ? "bg-green-50" : ""}`} data-testid="link-ab-b">
                        <span className="truncate"><span className="font-bold text-slate-700 mr-1">B</span><a href={bcDetail.link_b} target="_blank" rel="noreferrer" className="text-teal-700 hover:underline">{bcDetail.link_b}</a></span>
                        <span className="shrink-0 font-bold text-slate-900">{bcDetail.clicks_b ?? 0} clicks</span>
                      </div>
                    </div>
                  </div>
                )}
                {bcDetail.links && bcDetail.links.length > 0 && (
                  <div data-testid="broadcast-detail-links">
                    <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Top link clicks</h4>
                    <div className="rounded-xl border border-slate-200 overflow-hidden">
                      <div className="p-2 bg-slate-50/60" data-testid="broadcast-link-heatmap">
                        <ResponsiveContainer width="100%" height={Math.max(70, bcDetail.links.length * 34)}>
                          <BarChart data={bcDetail.links.slice(0, 8).map((lk) => ({ name: lk.url.replace(/^https?:\/\//, "").slice(0, 26), clicks: lk.clicks }))} layout="vertical" margin={{ left: 8, right: 16 }}>
                            <XAxis type="number" allowDecimals={false} hide />
                            <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11, fill: "#475569" }} />
                            <Tooltip cursor={{ fill: "#f1f5f9" }} />
                            <Bar dataKey="clicks" fill="#0d9488" radius={[0, 4, 4, 0]} barSize={16} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="divide-y divide-slate-100 border-t border-slate-200">
                        {bcDetail.links.map((lk, li) => (
                          <div key={li} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`broadcast-link-row-${li}`}>
                            <a href={lk.url} target="_blank" rel="noreferrer" className="text-teal-700 hover:underline truncate">{lk.url}</a>
                            <span className="shrink-0 font-bold text-slate-900">{lk.clicks} {lk.clicks === 1 ? "click" : "clicks"}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={!!digestPreview} onOpenChange={(o) => !o && setDigestPreview(null)}>
          <DialogContent data-testid="digest-preview-dialog">
            <DialogHeader>
              <DialogTitle>Weekly digest preview</DialogTitle>
              <DialogDescription>This is exactly what would be emailed to you — the last 7 days.</DialogDescription>
            </DialogHeader>
            {digestPreview && (
              <div className="space-y-4">
                <div className="grid grid-cols-4 gap-2">
                  {[["Broadcasts", digestPreview.broadcasts], ["Delivered", digestPreview.sent], ["Opens", digestPreview.opens], ["Clicks", digestPreview.clicks]].map(([label, val]) => (
                    <div key={label} className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-center" data-testid={`digest-stat-${label.toLowerCase()}`}>
                      <div className="text-2xl font-bold text-teal-600">{val ?? 0}</div>
                      <div className="text-[11px] text-slate-500">{label}</div>
                    </div>
                  ))}
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Top links</h4>
                  {(digestPreview.top_links || []).length > 0 ? (
                    <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden">
                      {digestPreview.top_links.map((l, i) => (
                        <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                          <span className="text-teal-700 truncate">{l.url}</span>
                          <span className="shrink-0 font-bold text-slate-900">{l.clicks}</span>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-sm text-slate-400">No link clicks this week.</p>}
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Top broadcasts</h4>
                  {(digestPreview.top_broadcasts || []).length > 0 ? (
                    <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden">
                      {digestPreview.top_broadcasts.map((b, i) => (
                        <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                          <span className="text-slate-700 truncate">{b.subject}</span>
                          <span className="shrink-0 text-slate-500">{b.opens} opens · {b.clicks} clicks</span>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-sm text-slate-400">No broadcasts sent this week.</p>}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <Dialog open={!!statDetail} onOpenChange={(o) => !o && setStatDetail(null)}>
        <DialogContent data-testid="stat-detail-dialog">
          <DialogHeader>
            <DialogTitle>{statDetail?.title}</DialogTitle>
            <DialogDescription>
              {statDetail?.type === "visits"
                ? "Daily visits and unique visitors over the last 30 days."
                : "Newest first. This is aggregate usage data — no personal identity is stored."}
            </DialogDescription>
          </DialogHeader>

          {statDetail?.type === "visits" && (
            <div data-testid="stat-detail-visits">
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={data.visits?.trend || []} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="visitFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366f1" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={(d) => (d || "").slice(5)} tickLine={false} axisLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} width={30} />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="visits" name="Visits" stroke="#6366f1" strokeWidth={2} fill="url(#visitFill)" />
                  <Area type="monotone" dataKey="unique" name="Unique" stroke="#0d9488" strokeWidth={2} fill="none" />
                </AreaChart>
              </ResponsiveContainer>
              <p className="text-sm text-slate-500 mt-2">
                Total {(data.visits?.total ?? 0).toLocaleString()} · {data.visits?.today ?? 0} today · {data.visits?.unique_today ?? 0} unique today
              </p>
            </div>
          )}

          {statDetail?.type !== "visits" && (
            <div className="max-h-96 overflow-y-auto" data-testid="stat-detail-questions">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap" data-testid="stat-range-bar">
                  <div className="flex items-center gap-2">
                    <div className="inline-flex rounded-lg border-2 border-slate-200 overflow-hidden text-xs font-semibold">
                      {[[7, "7d"], [30, "30d"], [90, "90d"], [0, "All"]].map(([d, lbl]) => (
                        <button
                          key={d}
                          onClick={() => changeStatRange(d)}
                          data-testid={`stat-range-${d}`}
                          className={`px-2.5 py-1.5 transition-colors ${statDays === d ? "bg-teal-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                        >
                          {lbl}
                        </button>
                      ))}
                    </div>
                    <select
                      value={statCategory}
                      onChange={(e) => changeStatCategory(e.target.value)}
                      data-testid="stat-category-select"
                      className="bg-white border-2 border-slate-200 focus:border-teal-400 rounded-lg px-2 py-1.5 text-xs font-semibold text-slate-700 outline-none"
                    >
                      <option value="">All categories</option>
                      {(data.categories || []).map((c) => (
                        <option key={c.id || c.label} value={c.id || c.label}>{c.label}</option>
                      ))}
                    </select>
                    <button
                      onClick={toggleStatTranslate}
                      data-testid="stat-translate-toggle"
                      title="Translate non-English questions to English"
                      className={`inline-flex items-center gap-1.5 rounded-lg border-2 px-2.5 py-1.5 text-xs font-semibold transition-colors ${statTranslate ? "bg-indigo-600 border-indigo-600 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                    >
                      <Languages className="w-3.5 h-3.5" /> English
                    </button>
                  </div>
                  {statRows && statRows.length > 0 && (
                    <button onClick={exportStatCsv} disabled={exportingStat} data-testid="stat-export-csv" className="inline-flex items-center gap-1.5 border-2 border-teal-300 text-teal-700 hover:bg-teal-50 disabled:opacity-60 font-semibold text-xs px-2.5 py-1.5 rounded-lg transition-colors">
                      {exportingStat ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Export CSV
                    </button>
                  )}
                </div>
              {statRows === null ? (
                <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : statRows.length === 0 ? (
                <p className="text-sm text-slate-400 py-6 text-center">
                  {statDetail?.type === "emergency" ? "No emergency-flagged questions yet." : "No questions recorded yet."}
                </p>
              ) : (
                <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden">
                  {statRows.map((q, i) => (
                    <div key={i} className="px-3 py-2.5 text-sm" data-testid={`stat-question-row-${i}`}>
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-slate-800 flex items-start gap-1.5">
                          {q.emergency_flag && <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />}
                          <span>
                            {statTranslate && q.question_text_en ? q.question_text_en : q.question_text}
                            {statTranslate && q.question_text_en && q.question_text_en !== q.question_text && (
                              <span className="block text-xs text-slate-400 mt-0.5" data-testid="stat-question-original">original: {q.question_text}</span>
                            )}
                          </span>
                        </span>
                        <span className="shrink-0 text-xs text-slate-400 font-mono">{q.date_created ? new Date(q.date_created).toLocaleDateString() : ""}</span>
                      </div>
                      {q.category && <span className="inline-flex mt-1 rounded-full bg-slate-100 text-slate-500 text-xs px-2 py-0.5">{q.category}</span>}
                    </div>
                  ))}
                </div>
              )}
              {statRows && statRows.length > 0 && (
                <p className="text-xs text-slate-400 mt-2">Showing {statRows.length} most recent.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard icon={MessageSquare} label="Questions asked" value={data.total_questions} tone="border-teal-200" testId="stat-total" onClick={() => openStat("questions")} />
        <StatCard icon={AlertTriangle} label="Emergency flags" value={data.emergency_flags} tone="border-red-200" testId="stat-emergency" onClick={() => openStat("emergency")} />
        <StatCard icon={ThumbsUp} label="Helpful (yes)" value={data.helpful_yes} tone="border-green-200" testId="stat-helpful-yes" />
        <StatCard icon={ThumbsDown} label="Not helpful (no)" value={data.helpful_no} tone="border-amber-200" testId="stat-helpful-no" />
        <div className="rounded-2xl border-2 p-5 bg-white border-indigo-200 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-transform" data-testid="stat-visits" role="button" tabIndex={0} onClick={() => openStat("visits")} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openStat("visits"); } }}>
          <div className="flex items-center gap-3">
            <span className="grid place-items-center w-11 h-11 rounded-xl bg-slate-100">
              <Eye className="w-6 h-6 text-slate-700" />
            </span>
            <div>
              <p className="text-3xl font-heading font-extrabold text-slate-900" data-testid="visits-total">
                {(data.visits?.total ?? 0).toLocaleString()}
              </p>
              <p className="text-base text-slate-500 font-medium">
                Site visits <span className="text-indigo-600 font-semibold">· {data.visits?.today ?? 0} today</span>
                {" · "}<span className="text-slate-400">{data.visits?.unique_today ?? 0} unique</span>
                <span className="ml-1 text-xs text-teal-600 font-semibold">· view</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border-2 border-slate-200 bg-white p-5 mb-8" data-testid="categories-chart">
        <h3 className="font-heading font-bold text-lg text-slate-900 mb-4">
          Most common question categories
        </h3>
        {data.categories && data.categories.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.categories} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" allowDecimals={false} stroke="#94a3b8" />
              <YAxis type="category" dataKey="label" width={130} stroke="#475569" tick={{ fontSize: 13 }} />
              <Tooltip cursor={{ fill: "#f1f5f9" }} />
              <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                {data.categories.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-base text-slate-400">No questions yet.</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <ListCard title="Most searched medications" icon={Pill} items={data.top_medications} emptyText="No medication questions yet." testId="top-medications" />
        <ListCard title="Most searched conditions" icon={HeartPulse} items={data.top_conditions} emptyText="No condition questions yet." testId="top-conditions" />
      </div>

      <div className="rounded-2xl border-2 border-slate-200 bg-white p-5" data-testid="unanswered-questions">
        <h3 className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2 mb-4">
          <HelpCircle className="w-5 h-5 text-teal-600" /> Unanswered Questions — turn into Library topics
        </h3>
        {data.unanswered && data.unanswered.length > 0 ? (
          <ul className="space-y-3">
            {data.unanswered.map((u, i) => {
              const state = addedTopics[u.question_text];
              return (
                <li key={i} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3 last:border-0" data-testid={`unanswered-${i}`}>
                  <span className="text-base text-slate-700">{u.question_text}</span>
                  {state && state !== "loading" ? (
                    <a
                      href={`/${state}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`topic-added-${i}`}
                      className="shrink-0 inline-flex items-center gap-1.5 bg-green-100 text-green-800 font-semibold text-sm px-3 py-1.5 rounded-lg"
                    >
                      <Check className="w-4 h-4" /> Added to Library
                    </a>
                  ) : (
                    <button
                      onClick={() => addTopic(u.question_text)}
                      disabled={state === "loading"}
                      data-testid={`add-topic-${i}`}
                      className="shrink-0 inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white font-semibold text-sm px-3 py-1.5 rounded-lg transition-colors"
                    >
                      {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      Add to Library
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-base text-slate-400">No unanswered questions. Great coverage!</p>
        )}
      </div>

      <div className="mt-6 rounded-2xl border-2 border-teal-200 bg-white p-5" data-testid="physicians-panel">
        <h3 className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2 mb-4">
          <Stethoscope className="w-5 h-5 text-teal-600" /> Physician Roster
          <span className="ml-1 bg-teal-100 text-teal-800 font-bold text-sm px-2.5 py-0.5 rounded-full">
            {physicians.filter((p) => p.active).length} active
          </span>
        </h3>
        <div className="flex flex-wrap items-end gap-3 mb-4 pb-4 border-b border-slate-100">
          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs font-semibold text-slate-500 mb-1">Name</label>
            <input
              value={newPhysName}
              onChange={(e) => setNewPhysName(e.target.value)}
              data-testid="physician-name-input"
              placeholder="Dr. Jane Smith"
              className="w-full bg-white border-2 border-slate-200 focus:border-teal-400 rounded-lg px-3 py-1.5 text-sm text-slate-800 outline-none"
            />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-semibold text-slate-500 mb-1">Email</label>
            <input
              value={newPhysEmail}
              onChange={(e) => setNewPhysEmail(e.target.value)}
              data-testid="physician-email-input"
              placeholder="dr@clinic.com"
              className="w-full bg-white border-2 border-slate-200 focus:border-teal-400 rounded-lg px-3 py-1.5 text-sm text-slate-800 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Handles</label>
            <select
              value={newPhysTier}
              onChange={(e) => setNewPhysTier(e.target.value)}
              data-testid="physician-tier-select"
              className="bg-white border-2 border-slate-200 focus:border-teal-400 rounded-lg px-3 py-1.5 text-sm font-medium text-slate-800 outline-none"
            >
              <option value="all">All reviews</option>
              <option value="reviewed_answer">Standard only</option>
              <option value="lab_review">Lab reviews only</option>
            </select>
          </div>
          <button
            onClick={handleAddPhysician}
            disabled={addingPhys}
            data-testid="add-physician-btn"
            className="inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm px-4 py-1.5 rounded-lg transition-colors disabled:opacity-60"
          >
            {addingPhys ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Add
          </button>
        </div>
        {physicians.length === 0 ? (
          <p className="text-sm text-slate-500">No physicians yet. Add one so paid requests route automatically.</p>
        ) : (
          <ul className="space-y-2" data-testid="physician-list">
            {physicians.map((p) => (
              <li key={p.physician_id} className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-50 last:border-0">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">
                    {p.name} <span className="font-normal text-slate-400">· {p.email}</span>
                  </p>
                  <p className="text-xs text-slate-500">
                    {(p.tiers || []).includes("all") ? "All reviews" : (p.tiers || []).includes("lab_review") ? "Lab reviews only" : "Standard only"}
                    {" · "}{p.open_load} open
                  </p>
                </div>
                <button
                  onClick={() => handleTogglePhysician(p)}
                  data-testid={`toggle-physician-${p.physician_id}`}
                  className={`text-xs font-bold px-2.5 py-1 rounded-full transition-colors ${
                    p.active ? "bg-green-100 text-green-800 hover:bg-green-200" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                >
                  {p.active ? "Active" : "Inactive"}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {(() => {
        const requests = (data.doctor_requests || []).filter(
          (r) => (showReviewed || r.status !== "reviewed") && (!labOnly || r.tier === "lab_review")
        );
        const openCount = data.doctor_open_count ?? (data.doctor_requests || []).filter((r) => r.status !== "reviewed").length;
        const reviewedCount = data.doctor_reviewed_count ?? (data.doctor_requests || []).filter((r) => r.status === "reviewed").length;
        const paidCount = data.doctor_paid_count ?? (data.doctor_requests || []).filter((r) => r.paid && !r.refunded).length;
        const refundedCount = data.doctor_refunded_count ?? (data.doctor_requests || []).filter((r) => r.refunded).length;
        const labPending = (data.doctor_requests || []).filter((r) => r.tier === "lab_review" && r.status !== "reviewed").length;
        return (
          <div className="mt-6 rounded-2xl border-2 border-sky-200 bg-white p-5" data-testid="doctor-requests-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2 flex-wrap">
                <UserPlus className="w-5 h-5 text-sky-600" /> Physician Review Requests
                <span className="ml-1 bg-sky-100 text-sky-800 font-bold text-sm px-2.5 py-0.5 rounded-full">
                  {openCount} open
                </span>
                <span className="bg-green-100 text-green-800 font-bold text-sm px-2.5 py-0.5 rounded-full" data-testid="reviewed-count">
                  {reviewedCount} reviewed
                </span>
                <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 font-bold text-sm px-2.5 py-0.5 rounded-full" data-testid="paid-count">
                  <CreditCard className="w-3.5 h-3.5" /> {paidCount} paid
                </span>
                {refundedCount > 0 && (
                  <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 font-bold text-sm px-2.5 py-0.5 rounded-full" data-testid="refunded-count">
                    <RotateCcw className="w-3.5 h-3.5" /> {refundedCount} refunded
                  </span>
                )}
                {labPending > 0 && (
                  <span className="inline-flex items-center gap-1 bg-violet-100 text-violet-800 font-bold text-sm px-2.5 py-0.5 rounded-full" data-testid="lab-pending-count">
                    <FileText className="w-3.5 h-3.5" /> {labPending} lab pending
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setLabOnly((v) => !v)}
                  data-testid="toggle-lab-filter"
                  className={`inline-flex items-center gap-2 text-sm font-semibold rounded-lg px-3 py-1.5 border transition-colors ${
                    labOnly
                      ? "bg-violet-600 text-white border-violet-600"
                      : "text-violet-700 hover:text-violet-900 border-violet-200"
                  }`}
                >
                  <FileText className="w-4 h-4" />
                  {labOnly ? "Showing lab only" : "Lab reviews only"}
                </button>
                <button
                  onClick={() => setShowReviewed((v) => !v)}
                  data-testid="toggle-reviewed-filter"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-sky-700 hover:text-sky-900 border border-sky-200 rounded-lg px-3 py-1.5"
                >
                  {showReviewed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {showReviewed ? "Hide reviewed" : "Show reviewed"}
                </button>
              </div>
            </div>
            {requests.length > 0 ? (
              <ul className="space-y-3">
                {requests.map((r, i) => (
                  <li key={r.request_id || i} className="border-b border-slate-100 pb-3 last:border-0" data-testid={`doctor-request-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        {r.tier && (
                          <span
                            data-testid={`tier-tag-${i}`}
                            className={`inline-flex items-center gap-1 font-bold text-xs px-2 py-0.5 rounded-full mb-1 ${
                              r.tier === "lab_review"
                                ? "bg-violet-100 text-violet-800"
                                : "bg-sky-100 text-sky-800"
                            }`}
                          >
                            {r.tier === "lab_review" ? (
                              <><FileText className="w-3 h-3" /> Lab review</>
                            ) : (
                              <><Stethoscope className="w-3 h-3" /> Reviewed answer</>
                            )}
                          </span>
                        )}
                        <p className="text-base text-slate-800">{r.question || "(no question text)"}</p>
                        {r.paid && !r.refunded && r.status !== "reviewed" && (
                          <div className="flex flex-wrap items-center gap-2 mt-1.5" data-testid={`assignment-${i}`}>
                            {r.assigned_physician_name ? (
                              <span className="inline-flex items-center gap-1 bg-teal-50 text-teal-700 font-semibold text-xs px-2 py-0.5 rounded-full">
                                <Stethoscope className="w-3 h-3" /> {r.assigned_physician_name}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 font-semibold text-xs px-2 py-0.5 rounded-full">
                                Unassigned
                              </span>
                            )}
                            <select
                              value=""
                              disabled={reassigning[r.request_id]}
                              onChange={(e) => handleReassign(r.request_id, e.target.value)}
                              data-testid={`reassign-select-${i}`}
                              className="bg-white border border-slate-200 focus:border-teal-400 rounded-md px-2 py-0.5 text-xs text-slate-700 outline-none"
                            >
                              <option value="">{r.assigned_physician_name ? "Reassign to…" : "Assign to…"}</option>
                              {physicians.filter((p) => p.active && ((p.tiers || []).includes("all") || (p.tiers || []).includes(r.tier))).map((p) => (
                                <option key={p.physician_id} value={p.physician_id}>{p.name}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {r.refunded ? (
                          <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 font-bold text-xs px-2.5 py-1 rounded-full" data-testid={`refunded-badge-${i}`}>
                            <RotateCcw className="w-3.5 h-3.5" /> Refunded
                          </span>
                        ) : r.paid && (
                          <>
                            <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 font-bold text-xs px-2.5 py-1 rounded-full" data-testid={`paid-badge-${i}`}>
                              <CreditCard className="w-3.5 h-3.5" /> Paid {r.tier === "lab_review" ? "$39" : "$19"}
                            </span>
                            <button
                              onClick={() => { setRefundFormId(r.request_id); setRefundReason(""); setRefundNotify(true); }}
                              disabled={refunding[r.request_id]}
                              data-testid={`refund-${i}`}
                              className="inline-flex items-center gap-1.5 border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-60 font-semibold text-sm px-3 py-1.5 rounded-lg transition-colors"
                            >
                              {refunding[r.request_id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                              Refund
                            </button>
                          </>
                        )}
                        {r.status === "reviewed" ? (
                          <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 font-bold text-xs px-2.5 py-1 rounded-full" data-testid={`doctor-status-${i}`}>
                            <Check className="w-3.5 h-3.5" /> Reviewed
                          </span>
                        ) : (
                          <>
                            {r.status === "reopened" && (
                              <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 font-bold text-xs px-2.5 py-1 rounded-full" data-testid={`reopened-badge-${i}`}>
                                <MessageSquare className="w-3.5 h-3.5" /> Visitor replied
                              </span>
                            )}
                            <button
                              onClick={() => { setReviewFormId(r.request_id); setReviewNote(""); setReviewPhotos([]); }}
                              data-testid={`mark-reviewed-${i}`}
                              className="inline-flex items-center gap-1.5 bg-sky-600 hover:bg-sky-700 text-white font-semibold text-sm px-3 py-1.5 rounded-lg transition-colors"
                            >
                              <Check className="w-4 h-4" /> Mark reviewed
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                      <span className="inline-flex items-center gap-1.5">
                        <Mail className="w-4 h-4" />
                        {r.contact ? r.contact : "anonymous (no contact given)"}
                      </span>
                      {r.date_created && <span>{new Date(r.date_created).toLocaleString()}</span>}
                      {r.status !== "reviewed" && r.date_created && (() => {
                        const w = waitingInfo(r.date_created);
                        return (
                          <span
                            data-testid={`sla-timer-${i}`}
                            className={`inline-flex items-center gap-1 font-bold text-xs px-2 py-0.5 rounded-full ${w.cls}`}
                          >
                            <Clock className="w-3.5 h-3.5" /> Waiting {w.label}
                          </span>
                        );
                      })()}
                    </div>
                    {Array.isArray(r.thread) && r.thread.length > 0 && (
                      <div className="mt-2 space-y-1.5" data-testid={`thread-${i}`}>
                        {r.thread.map((m, j) => (
                          <div
                            key={j}
                            className={`text-sm rounded-lg px-3 py-1.5 ${
                              m.role === "physician"
                                ? "bg-teal-50 text-slate-700"
                                : "bg-amber-50 text-slate-700"
                            }`}
                          >
                            <span className="font-semibold text-slate-600">
                              {m.role === "physician" ? "Physician: " : "Visitor: "}
                            </span>
                            {m.text}
                            {(Array.isArray(m.attachments) && m.attachments.length
                              ? m.attachments
                              : m.attachment_path
                              ? [{ path: m.attachment_path, name: m.attachment_name }]
                              : []
                            ).length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-1.5" data-testid={`thread-attachment-${i}-${j}`}>
                                {(Array.isArray(m.attachments) && m.attachments.length
                                  ? m.attachments
                                  : [{ path: m.attachment_path, name: m.attachment_name }]
                                ).map((a, k) =>
                                  /\.pdf$/i.test(a.path || a.name || "") ? (
                                    <a key={k} href={fileUrl(a.path, a.sig)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-700">
                                      <FileText className="w-4 h-4 text-red-500" /> {a.name || "PDF"}
                                    </a>
                                  ) : (
                                    <a key={k} href={fileUrl(a.path, a.sig)} target="_blank" rel="noreferrer">
                                      <img
                                        src={fileUrl(a.path, a.sig)}
                                        alt={a.name || "attachment"}
                                        className="max-h-40 rounded-lg border border-slate-200"
                                      />
                                    </a>
                                  )
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {(!r.thread || r.thread.length === 0) && r.status === "reviewed" && r.review_note && (
                      <p className="mt-1 text-sm text-slate-500" data-testid={`review-note-${i}`}>
                        <span className="font-semibold text-slate-600">Physician note:</span> {r.review_note}
                      </p>
                    )}
                    {r.refunded && r.refund_reason && (
                      <p className="mt-1 text-sm text-slate-500" data-testid={`refund-reason-${i}`}>
                        <span className="font-semibold text-slate-600">Refund reason:</span> {r.refund_reason}
                      </p>
                    )}
                    {reviewFormId === r.request_id && (
                      <div className="mt-3 rounded-xl border border-sky-200 bg-sky-50 p-3" data-testid={`review-form-${i}`}>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                          Short answer for the visitor (optional — emailed to them)
                        </label>
                        <textarea
                          value={reviewNote}
                          onChange={(e) => setReviewNote(e.target.value)}
                          rows={3}
                          placeholder="e.g. Your reading is in a safe range. Keep monitoring and follow up with your doctor at your next visit."
                          data-testid={`review-note-input-${i}`}
                          className="w-full bg-white border-2 border-sky-200 focus:border-sky-400 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 outline-none resize-y"
                        />
                        {reviewPhotos.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2" data-testid={`review-photos-preview-${i}`}>
                            {reviewPhotos.map((p, k) => (
                              <div key={k} className="relative">
                                <img src={URL.createObjectURL(p)} alt="preview" className="w-14 h-14 object-cover rounded-lg border border-slate-200" />
                                <button
                                  onClick={() => setReviewPhotos((prev) => prev.filter((_, idx) => idx !== k))}
                                  className="absolute -top-2 -right-2 bg-white border border-slate-300 rounded-full p-0.5 text-slate-500 hover:text-slate-800 shadow-sm"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <input ref={reviewFileRef} type="file" accept="image/*" multiple onChange={pickReviewPhoto} data-testid={`review-photo-input-${i}`} className="hidden" />
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            onClick={() => markReviewed(r.request_id)}
                            disabled={reviewing[r.request_id]}
                            data-testid={`confirm-reviewed-${i}`}
                            className="inline-flex items-center gap-1.5 bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white font-semibold text-sm px-3 py-1.5 rounded-lg transition-colors"
                          >
                            {reviewing[r.request_id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            Confirm reviewed
                          </button>
                          <button
                            onClick={() => reviewFileRef.current && reviewFileRef.current.click()}
                            disabled={reviewPhotos.length >= 3}
                            data-testid={`review-add-photo-${i}`}
                            className="inline-flex items-center gap-1.5 border border-sky-300 text-sky-700 hover:bg-sky-100 disabled:opacity-50 font-semibold text-sm px-3 py-1.5 rounded-lg transition-colors"
                          >
                            <ImagePlus className="w-4 h-4" /> Add photo{reviewPhotos.length ? ` (${reviewPhotos.length}/3)` : ""}
                          </button>
                          <button
                            onClick={() => { setReviewFormId(null); setReviewNote(""); setReviewPhotos([]); }}
                            data-testid={`cancel-reviewed-${i}`}
                            className="text-sm font-semibold text-slate-600 hover:text-slate-900 px-3 py-1.5"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                    {refundFormId === r.request_id && (
                      <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid={`refund-form-${i}`}>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                          Reason for refund (optional)
                        </label>
                        <input
                          value={refundReason}
                          onChange={(e) => setRefundReason(e.target.value)}
                          placeholder="e.g. duplicate payment, question withdrawn"
                          data-testid={`refund-reason-input-${i}`}
                          className="w-full bg-white border-2 border-amber-200 focus:border-amber-400 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 outline-none"
                        />
                        <label className="mt-2 flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={refundNotify}
                            onChange={(e) => setRefundNotify(e.target.checked)}
                            data-testid={`refund-notify-${i}`}
                            className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-400"
                          />
                          Email the visitor about this refund
                        </label>
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            onClick={() => refund(r.request_id)}
                            disabled={refunding[r.request_id]}
                            data-testid={`confirm-refund-${i}`}
                            className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white font-semibold text-sm px-3 py-1.5 rounded-lg transition-colors"
                          >
                            {refunding[r.request_id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                            Confirm refund {r.tier === "lab_review" ? "$39" : "$19"}
                          </button>
                          <button
                            onClick={() => { setRefundFormId(null); setRefundReason(""); }}
                            data-testid={`cancel-refund-${i}`}
                            className="text-sm font-semibold text-slate-600 hover:text-slate-900 px-3 py-1.5"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-base text-slate-400">
                {showReviewed ? "No physician review requests yet." : "No open requests. 🎉"}
              </p>
            )}
          </div>
        );
      })()}

      {data.upsell_ab && (
        <div className="mt-6 rounded-2xl border-2 border-slate-200 bg-white p-5" data-testid="upsell-ab-card">
          <h3 className="font-heading font-bold text-lg text-slate-900 mb-1">Upsell A/B — "$19 reviewed" copy</h3>
          <p className="text-sm text-slate-500 mb-4">Which wording earns more paid reviews. Conversion = paid reviews ÷ clicks.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {["A", "B"].map((v) => {
              const s = data.upsell_ab[v] || { clicks: 0, reviews: 0 };
              const conv = s.clicks ? Math.round((s.reviews / s.clicks) * 100) : 0;
              return (
                <div key={v} className="rounded-xl border border-slate-200 p-4" data-testid={`upsell-variant-${v}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-slate-800">Variant {v}</span>
                    <span className="text-sm font-bold text-emerald-700">{conv}% conv.</span>
                  </div>
                  <div className="flex gap-4 text-sm text-slate-600">
                    <span><strong className="text-slate-900">{s.clicks}</strong> clicks</span>
                    <span><strong className="text-slate-900">{s.reviews}</strong> paid reviews</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.upsell_trend && data.upsell_trend.length > 0 && (
        <div className="mt-6 rounded-2xl border-2 border-slate-200 bg-white p-5" data-testid="upsell-trend-card">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
            <h3 className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-teal-600" /> Upsell conversions over time
            </h3>
            {(() => {
              const ab = data.upsell_ab || {};
              const convOf = (v) => {
                const s = ab[v] || { clicks: 0, reviews: 0 };
                return s.clicks ? s.reviews / s.clicks : 0;
              };
              const w = data.upsell_winner || {};
              const hasData = ((ab.A && ab.A.clicks) || 0) + ((ab.B && ab.B.clicks) || 0) > 0;
              let leader = w.variant;
              if (!leader && hasData) leader = convOf("A") >= convOf("B") ? "A" : "B";
              if (w.locked && leader) {
                return (
                  <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-800 font-bold text-sm px-3 py-1 rounded-full" data-testid="upsell-winner-badge">
                    <Trophy className="w-4 h-4" /> Variant {leader} winner
                  </span>
                );
              }
              if (leader) {
                return (
                  <span className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-800 font-bold text-sm px-3 py-1 rounded-full" data-testid="upsell-leader-badge">
                    <Trophy className="w-4 h-4" /> Variant {leader} leading
                  </span>
                );
              }
              return (
                <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-500 font-semibold text-sm px-3 py-1 rounded-full" data-testid="upsell-nodata-badge">
                  Gathering data
                </span>
              );
            })()}
          </div>
          <p className="text-sm text-slate-500 mb-4">Daily upsell clicks and paid reviews over the last 30 days.</p>
          {data.revenue && (
            <div className="grid grid-cols-3 gap-3 mb-5" data-testid="revenue-snapshot">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3" data-testid="revenue-collected">
                <p className="text-2xl font-heading font-extrabold text-emerald-700">${data.revenue.collected}</p>
                <p className="text-sm font-medium text-slate-500 flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> Collected</p>
              </div>
              <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3" data-testid="revenue-refunded">
                <p className="text-2xl font-heading font-extrabold text-amber-700">${data.revenue.refunded}</p>
                <p className="text-sm font-medium text-slate-500 flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" /> Refunded</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid="revenue-net">
                <p className="text-2xl font-heading font-extrabold text-slate-900">${data.revenue.net}</p>
                <p className="text-sm font-medium text-slate-500">Net</p>
              </div>
            </div>
          )}
          {data.revenue && data.settings && (() => {
            const goal = data.settings.revenue_goal;
            const month = data.revenue.month_collected || 0;
            const pct = goal ? Math.min(100, Math.round((month / goal) * 100)) : 0;
            return (
              <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50/40 px-4 py-4" data-testid="revenue-goal">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
                    <Target className="w-4 h-4 text-emerald-600" /> Monthly revenue goal
                  </span>
                  {!editingGoal && (
                    <button
                      onClick={() => { setEditingGoal(true); setGoalInput(goal || ""); }}
                      data-testid="edit-goal-btn"
                      className="text-xs font-semibold text-emerald-700 hover:text-emerald-900"
                    >
                      {goal ? "Edit goal" : "Set a goal"}
                    </button>
                  )}
                </div>
                {editingGoal ? (
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                      <input
                        type="number"
                        min="0"
                        value={goalInput}
                        onChange={(e) => setGoalInput(e.target.value)}
                        placeholder="e.g. 500"
                        data-testid="goal-input"
                        className="w-32 pl-6 pr-3 py-1.5 bg-white border-2 border-slate-200 focus:border-emerald-400 rounded-lg text-sm outline-none"
                      />
                    </div>
                    <button
                      onClick={saveGoal}
                      disabled={savingGoal}
                      data-testid="save-goal-btn"
                      className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold text-sm px-3 py-1.5 rounded-lg transition-colors"
                    >
                      {savingGoal ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      Save
                    </button>
                    <button
                      onClick={() => setEditingGoal(false)}
                      className="text-sm font-semibold text-slate-500 hover:text-slate-800 px-2"
                    >
                      Cancel
                    </button>
                  </div>
                ) : goal ? (
                  <>
                    <div className="flex items-center justify-between text-sm font-semibold text-slate-600 mb-1.5">
                      <span>${month} collected this month</span>
                      <span data-testid="goal-percent">{pct}% of ${goal}</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-emerald-100 overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 transition-all"
                        style={{ width: `${pct}%` }}
                        data-testid="goal-progress-bar"
                      />
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-slate-400">
                    No goal set yet — ${month} collected so far this month.
                  </p>
                )}
              </div>
            );
          })()}
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={data.upsell_trend} margin={{ left: -12, right: 8, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="date" tickFormatter={fmtDay} stroke="#94a3b8" tick={{ fontSize: 11 }} minTickGap={26} />
              <YAxis allowDecimals={false} stroke="#94a3b8" tick={{ fontSize: 11 }} />
              <Tooltip content={<TrendTooltip />} cursor={{ fill: "#f8fafc" }} />
              <Legend wrapperStyle={{ fontSize: 13 }} />
              <Bar dataKey="clicks" name="Upsell clicks" fill="#bae6fd" radius={[4, 4, 0, 0]} barSize={14} />
              <Line type="monotone" dataKey="reviews" name="Paid reviews" stroke="#0d9488" strokeWidth={2.5} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
          {data.revenue_trend && data.revenue_trend.length > 0 && (
            <div className="mt-6 pt-5 border-t border-slate-100" data-testid="revenue-trend">
              <h4 className="font-heading font-bold text-base text-slate-800 flex items-center gap-2 mb-3">
                <DollarSign className="w-4 h-4 text-emerald-600" /> Daily revenue collected (last 30 days)
              </h4>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={data.revenue_trend} margin={{ left: -8, right: 8, top: 4 }}>
                  <defs>
                    <linearGradient id="revStd" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0d9488" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#0d9488" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="revLab" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#7c3aed" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#7c3aed" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={fmtDay} stroke="#94a3b8" tick={{ fontSize: 11 }} minTickGap={26} />
                  <YAxis allowDecimals={false} stroke="#94a3b8" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip formatter={(v, n) => [`$${v}`, n]} labelFormatter={fmtDay} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="standard" name="Reviews ($19)" stackId="1" stroke="#0d9488" strokeWidth={2} fill="url(#revStd)" />
                  <Area type="monotone" dataKey="lab" name="Lab reviews ($39)" stackId="1" stroke="#7c3aed" strokeWidth={2} fill="url(#revLab)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {data.visits?.trend && data.visits.trend.length > 0 && (
        <div className="mt-6 rounded-2xl border-2 border-indigo-200 bg-white p-5" data-testid="visits-trend">
          <h3 className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2 mb-4">
            <Eye className="w-5 h-5 text-indigo-600" /> Site visits (last 30 days)
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={data.visits.trend} margin={{ left: -8, right: 8, top: 4 }}>
              <defs>
                <linearGradient id="visTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="visUnique" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0d9488" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#0d9488" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="date" tickFormatter={fmtDay} stroke="#94a3b8" tick={{ fontSize: 11 }} minTickGap={26} />
              <YAxis allowDecimals={false} stroke="#94a3b8" tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={fmtDay} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="visits" name="Visits" stroke="#6366f1" strokeWidth={2} fill="url(#visTotal)" />
              <Area type="monotone" dataKey="unique" name="Unique visitors" stroke="#0d9488" strokeWidth={2} fill="url(#visUnique)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}


      {settings && (
        <div className="mt-6 rounded-2xl border-2 border-slate-200 bg-white p-5" data-testid="digest-settings-card">
          <h3 className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2 mb-1">
            <Clock className="w-5 h-5 text-teal-600" /> Overdue-review email digest
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            Choose when your once-a-day summary of lab reviews waiting past 48h arrives.
            {TZ ? ` Times shown in your timezone (${TZ}).` : ""}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-semibold text-slate-700">Send around</label>
            <select
              value={settings.digest_hour_local ?? ""}
              onChange={(e) => saveHour(e.target.value)}
              disabled={savingHour}
              data-testid="digest-hour-select"
              className="bg-white border-2 border-slate-200 focus:border-teal-400 rounded-lg px-3 py-2 text-sm font-medium text-slate-800 outline-none disabled:opacity-60"
            >
              <option value="">First check of the day (default)</option>
              {Array.from({ length: 24 }).map((_, h) => (
                <option key={h} value={h}>{hourLabel(h)}</option>
              ))}
            </select>
            {savingHour && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          </div>
          <button
            onClick={sendTest}
            disabled={sendingTest}
            data-testid="digest-test-btn"
            className="mt-4 inline-flex items-center gap-1.5 border border-teal-300 text-teal-700 hover:bg-teal-50 disabled:opacity-60 font-semibold text-sm px-4 py-2 rounded-lg transition-colors"
          >
            {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Send me a test digest now
          </button>
        </div>
      )}
    </div>
  );
}
